import axios from 'axios';

const API_URL = 'https://quotebid-backend-production.up.railway.app';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if it exists
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth endpoints
export const register = (data: { email: string; password: string; name: string; user_type: string; phone?: string; sms_consent?: boolean; captchaToken?: string | null }) =>
  api.post('/auth/register', data);
export const registerBusiness = (data: any) =>
  api.post('/auth/register-business', data);

export const login = (data: { email: string; password: string }) =>
  api.post('/auth/login', data);

export const forgotPassword = (email: string) =>
  api.post('/auth/forgot-password', { email });

export const resetPassword = (token: string, password: string) =>
  api.post('/auth/reset-password', { token, password });

export const consumeMagicLink = (token: string) =>
  api.post('/auth/magic-link/consume', { token });

// Quote request endpoints
export const createRequest = (data: any) =>
  api.post('/quotes/requests', data);

export const getRequests = () =>
  api.get('/quotes/requests');

export const getRequest = (id: number) =>
  api.get(`/quotes/requests/${id}`);

export const getMyRequests = () =>
  api.get('/quotes/my-requests');

// Quote endpoints
export const submitQuote = (data: any) =>
  api.post('/quotes/submit', data);

export const getMyQuotes = () =>
  api.get('/quotes/my-quotes');

export const acceptQuote = (quoteId: number) =>
  api.post(`/quotes/accept/${quoteId}`);

// QUOTE-INFO-REQUEST1 Phase C: a contractor asks the homeowner a question
// WITHOUT inventing a price. The parameter type is deliberately narrow rather
// than `any` (as submitQuote is) so a price field cannot be passed from a
// call site without a compile error. The backend independently rejects any
// price field with 400 INFO_REQUEST_HAS_PRICE.
export const submitInfoRequest = (data: { request_id: number; message: string }) =>
  api.post('/quotes/info-request', data);

// AI endpoints
export const analyzeRequest = (data: { title: string; description: string }) =>
  api.post('/ai/analyze', data);

export const categorizeRequest = (data: { title: string; description: string }) =>
  api.post('/ai/categorize', data);

// Resend verification
export const resendVerification = (email: string) =>
  api.post('/auth/resend-verification', { email });

// Profile endpoints
export const updateProfile = (data: { name?: string; phone?: string }) =>
  api.put('/auth/profile', data);

export const changeEmail = (data: { newEmail: string; password: string }) =>
  api.put('/auth/change-email', data);

export const changePassword = (data: { currentPassword: string; newPassword: string }) =>
  api.put('/auth/change-password', data);

export const updateBusinessCategories = (data: { categories: string[]; description?: string }) =>
  api.put('/auth/business-categories', data);

export const updateBusinessServiceCities = (data: { service_cities: string[]; description?: string }) =>
  api.put('/auth/business-service-cities', data);

export const updateBusinessLocation = (data: { address?: string; city?: string; state?: string; zip?: string }) =>
  api.put('/auth/business-location', data);

export const deleteAccount = (password: string) =>
  api.delete('/auth/delete-account', { data: { password } });

// Reputation endpoints
export const completeJob = (data: { quoteRequestId: number }) =>
  api.post('/api/reputation/complete', data);

export const rateJob = (data: { completionId: number; stars: number; comment?: string }) =>
  api.post('/api/reputation/rate', data);

export const getBadge = (userId: number) =>
  api.get(`/api/reputation/badge/${userId}`);

// Subscription endpoints
export const getFoundingMemberCount = () =>
  api.get('/subscription/founding-member-count');

// Source of truth for the signed-in business's plan tier (used by Settings
// to derive footprint limits — never trust localStorage for tier).
export const getSubscriptionStatus = () =>
  api.get('/subscription/status');

// Dispute endpoints
export const getAdminPendingDisputes = () =>
  api.get('/api/reputation/admin/disputes/pending');

export const resolveDispute = (
  disputeId: number,
  data: { resolution: 'upheld' | 'dismissed'; admin_notes?: string }
) => api.post(`/api/reputation/admin/disputes/${disputeId}/resolve`, data);

export const fileDispute = (data: {
  quote_request_id: number;
  reason_category: 'no_show' | 'poor_work' | 'safety' | 'other';
  description: string;
}) => api.post('/api/reputation/disputes', data);

export const getMyFiledDisputes = () =>
  api.get('/api/reputation/disputes/filed-by-me');

export default api;